"use client";

import {
  connectionHasActiveInvestmentAccount,
  connectionHasActiveNonInvestmentAccount,
  fetchManualConnections,
  fetchPlaidConnections,
  filterActiveFinancialConnections,
  logConnectionAccountStatuses,
} from "./memberPlaidApi";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import type { FinancialConnection } from "@/src/features/api/memberApi";
import { useMemoizedFn } from "ahooks";
import { useOspApiClientProvider } from "@/src/hooks/useOspApiClientProvider";

export const PORTFOLIO_CONNECTIONS_QUERY_KEY = [
  "portfolio-financial-connections",
] as const;

export interface UsePortfolioConnectionsResult {
  /** All active manual connections (not filtered by account type). */
  manualConnections: FinancialConnection[];

  /** Active connections with at least one INVESTMENT sub-account (plaid + manual merged). */
  investConnections: FinancialConnection[];

  /** Active connections with at least one non-INVESTMENT sub-account (plaid + manual merged). */
  nonInvestConnections: FinancialConnection[];

  /** Backward compat: active + INVESTMENT plaid connections. */
  plaidConnections: FinancialConnection[];
  /** Backward compat: active + INVESTMENT manual connections. */
  plaidManualConnections: FinancialConnection[];

  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
  invalidate: () => Promise<void>;
}

export const usePortfolioConnections = (): UsePortfolioConnectionsResult => {
  const { memberApi } = useOspApiClientProvider();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: PORTFOLIO_CONNECTIONS_QUERY_KEY,
    enabled: Boolean(memberApi),
    queryFn: async () => {
      if (!memberApi) {
        throw new Error("memberApi is not available");
      }

      const [plaidRaw, manualRaw] = await Promise.all([
        fetchPlaidConnections(memberApi),
        fetchManualConnections(memberApi),
      ]);

      // Log account status distribution for debugging
      plaidRaw.forEach((conn) => logConnectionAccountStatuses(conn, "PLAID"));
      manualRaw.forEach((conn) => logConnectionAccountStatuses(conn, "MANUAL"));

      const plaidActive = filterActiveFinancialConnections(plaidRaw);
      const manualActive = filterActiveFinancialConnections(manualRaw);
      const plaidInvest = plaidActive.filter(connectionHasActiveInvestmentAccount);
      const manualInvest = manualActive.filter(connectionHasActiveInvestmentAccount);
      const allActive = [...plaidActive, ...manualActive];

      return {
        // New: all active manual connections (not filtered by account type)
        manualConnections: manualActive,

        // New: merged invest/non-invest across plaid + manual (filtered by active accounts)
        investConnections: [...plaidInvest, ...manualInvest],
        nonInvestConnections: allActive.filter(
          connectionHasActiveNonInvestmentAccount,
        ),

        // Backward compat: active + INVESTMENT, by source (reuse filtered subsets)
        plaidConnections: plaidInvest,
        plaidManualConnections: manualInvest,
      };
    },
  });

  const invalidate = useMemoizedFn(async () => {
    await queryClient.invalidateQueries({
      queryKey: PORTFOLIO_CONNECTIONS_QUERY_KEY,
    });
  });

  return {
    manualConnections: query.data?.manualConnections ?? [],
    investConnections: query.data?.investConnections ?? [],
    nonInvestConnections: query.data?.nonInvestConnections ?? [],
    plaidConnections: query.data?.plaidConnections ?? [],
    plaidManualConnections: query.data?.plaidManualConnections ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error instanceof Error ? query.error : null,
    refetch: query.refetch,
    invalidate,
  };
};
